import { AccountVariable } from '@/client/accounts';
import {
  buildPointOfContactContent,
  POINT_OF_CONTACT_TOOLTIP_CLASSES,
  resolvePointOfContact,
} from '@/utils/helpers/point-of-contact-tooltip.helper';
import {
  POINT_OF_CONTACT_FORMAT,
  POINT_OF_CONTACT_TYPE_ATTRIBUTE,
} from '@/utils/helpers/point-of-contact.helper';
import {
  addCaretToTooltip,
  closeAllTooltips,
  createTooltipContainer,
  getTargetRectViewportForElement,
  isInsideSummaryContainer,
  positionAndStyleTooltip,
  removeExistingTooltips,
} from '@/utils/helpers/tooltip-dom.helper';
import { t } from 'i18next';
import Quill from 'quill';
import type React from 'react';
import { clearPointOfContact } from './point-of-contact';

// Check if Quill container is visible
const checkQuillVisibility = (quillContainer: HTMLElement) => {
  const rect = quillContainer.getBoundingClientRect();
  const isVisible =
    rect.width > 0 &&
    rect.height > 0 &&
    rect.top < window.innerHeight &&
    rect.bottom > 0;

  if (!isVisible) {
    closeAllTooltips();
  }
};

// Custom TooltipBlot for Quill
const Inline = Quill.import('blots/inline');
class TooltipBlot extends Inline {
  static create(value: string) {
    const node = super.create();
    node.setAttribute('data-quill-tooltip', value);
    node.classList.add('custom-tooltip');
    return node;
  }
  static formats(node: HTMLElement) {
    return node.getAttribute('data-quill-tooltip');
  }
  format(name: string, value: string | boolean) {
    if (name === 'tooltip') {
      if (value && typeof value === 'string') {
        this.domNode.setAttribute('data-quill-tooltip', value);
      } else {
        this.domNode.removeAttribute('data-quill-tooltip');
        this.domNode.classList.remove('custom-tooltip');
        const attributeCount = this.domNode.attributes.length;
        const classCount = this.domNode.classList.length;

        if (attributeCount === 0 && classCount === 0) {
          super.format(name, false);
        }
      }
    } else {
      super.format(name, value);
    }
  }
}
TooltipBlot.blotName = 'tooltip';
TooltipBlot.tagName = 'span';
Quill.register(TooltipBlot);

const isMultilineSelection = (
  quill: any,
  index: number,
  length: number,
): boolean => {
  const bounds = quill.getBounds(index, length);
  const singleCharBounds = quill.getBounds(index, 1);
  return bounds.height > singleCharBounds.height * 1.5;
};

const getFirstWordRect = (
  quill: any,
  quillContainer: HTMLElement,
  index: number,
  length: number,
): { top: number; left: number; width: number; height: number } | null => {
  const text = quill.getText(index, length);
  const words = text.trim().split(/\s+/);

  if (words.length === 0 || words[0].length === 0) {
    return null;
  }

  const firstWord = words[0];
  const firstWordIndex = index + text.indexOf(firstWord);
  const firstWordLength = firstWord.length;

  const bounds = quill.getBounds(firstWordIndex, firstWordLength);
  const containerRect = quillContainer.getBoundingClientRect();

  return {
    top: containerRect.top + bounds.top,
    left: containerRect.left + bounds.left,
    width: bounds.width,
    height: bounds.height,
  };
};

const getTargetRectViewportForQuill = (
  quill: any,
  quillContainer: HTMLElement,
  index: number,
  length: number,
  bounds: { top: number; left: number; width: number; height: number },
): { top: number; left: number; width: number; height: number } => {
  if (isMultilineSelection(quill, index, length)) {
    const firstWordRect = getFirstWordRect(
      quill,
      quillContainer,
      index,
      length,
    );
    if (firstWordRect) {
      return firstWordRect;
    }
  }
  const containerRect = quillContainer.getBoundingClientRect();

  return {
    top: containerRect.top + bounds.top,
    left: containerRect.left + bounds.left,
    width: bounds.width,
    height: bounds.height,
  };
};

// Helper function to create auto-resize textarea
const createAutoResizeTextarea = (
  value: string,
  placeholder: string,
  onResize?: () => void,
): HTMLTextAreaElement => {
  const textarea = document.createElement('textarea');
  textarea.className = 'quill-tooltip-input';
  textarea.rows = 1;
  textarea.style.overflow = 'hidden';
  textarea.value = value;
  textarea.setAttribute('placeholder', placeholder);

  const resize = () => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    if (onResize) onResize();
  };

  textarea.addEventListener('input', resize);
  setTimeout(resize, 0);
  return textarea;
};

export const setupTooltipButton = (
  quillRef: React.RefObject<any>,
  tooltipButton: HTMLButtonElement | null,
) => {
  if (tooltipButton) {
    tooltipButton.onclick = () => {
      const quill = quillRef.current;
      if (!quill) return;
      const range = quill.getSelection();
      if (range && range.length > 0) {
        const finalRange = range;
        const bounds = quill.getBounds(finalRange.index, finalRange.length);
        const quillContainer = quill.container;
        removeExistingTooltips();

        const isInSummary = isInsideSummaryContainer({
          quill,
          index: finalRange.index,
          length: finalRange.length,
        });

        const tooltipBox = createTooltipContainer(
          'quill-tooltip-input-container',
          isInSummary,
        );

        const targetRectViewport = getTargetRectViewportForQuill(
          quill,
          quillContainer,
          finalRange.index,
          finalRange.length,
          bounds,
        );

        const tooltipTextarea = createAutoResizeTextarea(
          '',
          t('generic.enterTooltipText'),
          () => {
            positionAndStyleTooltip(tooltipBox, targetRectViewport);
          },
        );
        tooltipBox.appendChild(tooltipTextarea);
        addCaretToTooltip(tooltipBox);
        document.body.appendChild(tooltipBox);

        positionAndStyleTooltip(tooltipBox, targetRectViewport);
        tooltipTextarea.focus();

        let isApplied = false;
        const applyTooltip = () => {
          if (isApplied) return;
          isApplied = true;

          const tooltipText = tooltipTextarea.value.trim();

          if (tooltipBox.isConnected) {
            tooltipTextarea.removeEventListener('keydown', keydownHandler);
            tooltipTextarea.removeEventListener('blur', applyTooltip);
            const parent = tooltipBox.parentNode;
            if (parent && parent.contains(tooltipBox)) {
              try {
                parent.removeChild(tooltipBox);
              } catch (error) {
                // Already removed
              }
            }
          }

          const allBlots: any[] = [];
          quill.scroll.descendants((blot: any) => {
            if (blot.statics.blotName === 'tooltip') {
              allBlots.push(blot);
            }
          });

          // Collect tooltips to remove
          const tooltipsToRemove: Array<{
            index: number;
            length: number;
            text: string;
          }> = [];

          // Remove tooltips in reverse order
          tooltipsToRemove
            .sort((a, b) => b.index - a.index)
            .forEach(({ index, length, text }) => {
              quill.setSelection(index, length);
              quill.deleteText(index, length);
              quill.insertText(index, text);
            });

          quill.setSelection(finalRange.index, finalRange.length);

          if (tooltipText) {
            clearPointOfContact(quill, finalRange.index, finalRange.length);
            quill.setSelection(finalRange.index, finalRange.length);
            quill.format('tooltip', tooltipText);
          } else {
            quill.setSelection(finalRange.index, finalRange.length);
          }

          quill.focus();
        };

        const keydownHandler = (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyTooltip();
          } else if (e.key === 'Escape') {
            if (isApplied) return;
            isApplied = true;

            if (tooltipBox.isConnected) {
              tooltipTextarea.removeEventListener('keydown', keydownHandler);
              tooltipTextarea.removeEventListener('blur', applyTooltip);
              const parent = tooltipBox.parentNode;
              if (parent && parent.contains(tooltipBox)) {
                parent.removeChild(tooltipBox);
              }
            }
            quill.setSelection(range.index, range.length);
            quill.focus();
          }
        };

        tooltipTextarea.addEventListener('keydown', keydownHandler);
        tooltipTextarea.addEventListener('blur', applyTooltip);
      }
    };
  }
};

// Show tooltip on hover (display only)
const showEditorHoverTooltip = (
  quill: any,
  quillContainer: HTMLElement,
  target: HTMLElement,
  content: HTMLElement,
  variantClassNames: string[] = [],
  rtl = false,
) => {
  const existingHover = document.body.querySelector(
    '.custom-tooltip-hover-display',
  );
  if (existingHover?.isConnected) {
    existingHover.remove();
  }

  const isInSummary = isInsideSummaryContainer(target);
  const hoverTooltip = createTooltipContainer(
    'custom-tooltip-hover-display',
    isInSummary,
  );
  if (variantClassNames.length > 0) {
    hoverTooltip.classList.add(...variantClassNames);
  }
  if (rtl) {
    hoverTooltip.setAttribute('dir', 'rtl');
  }
  hoverTooltip.appendChild(content);
  addCaretToTooltip(hoverTooltip);
  document.body.appendChild(hoverTooltip);

  const blot = Quill.find(target);
  if (blot) {
    const index = blot.offset(quill.scroll);
    const length = blot.length();
    positionAndStyleTooltip(
      hoverTooltip,
      getTargetRectViewportForQuill(
        quill,
        quillContainer,
        index,
        length,
        quill.getBounds(index, length),
      ),
    );
  } else {
    positionAndStyleTooltip(
      hoverTooltip,
      getTargetRectViewportForElement(target),
    );
  }

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const cancelRemove = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  const removeHover = () => {
    cancelRemove();
    timeoutId = setTimeout(() => {
      if (hoverTooltip.isConnected) {
        hoverTooltip.remove();
        target.removeEventListener('mouseleave', removeHover);
        hoverTooltip.removeEventListener('mouseenter', cancelRemove);
        hoverTooltip.removeEventListener('mouseleave', removeHover);
      }
    }, 100);
  };

  target.addEventListener('mouseleave', removeHover);
  hoverTooltip.addEventListener('mouseenter', cancelRemove);
  hoverTooltip.addEventListener('mouseleave', removeHover);
};

const handleTooltipHover = (
  quill: any,
  quillContainer: HTMLElement,
  e: Event,
) => {
  const target = e.target as HTMLElement;

  const pointOfContact = target?.closest?.<HTMLElement>(
    `.${POINT_OF_CONTACT_FORMAT}[${POINT_OF_CONTACT_TYPE_ATTRIBUTE}]`,
  );
  if (pointOfContact && quillContainer.contains(pointOfContact)) {
    if (document.body.querySelector('.custom-tooltip-edit-box')) return;

    const contact = resolvePointOfContact(
      (quillContainer as any)._pointOfContactVariables,
      pointOfContact.getAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE),
    );
    if (!contact) return;

    showEditorHoverTooltip(
      quill,
      quillContainer,
      pointOfContact,
      buildPointOfContactContent(
        contact,
        (quillContainer as any)._pointOfContactOnCopied,
      ),
      POINT_OF_CONTACT_TOOLTIP_CLASSES,
      (quillContainer as any)._pointOfContactRtl,
    );
    return;
  }

  if (
    target &&
    target.classList.contains('custom-tooltip') &&
    target.hasAttribute('data-quill-tooltip')
  ) {
    const existingEditBox = document.body.querySelector(
      '.custom-tooltip-edit-box',
    );
    if (existingEditBox) return;

    // Remove any existing hover tooltip
    const existingHover = document.body.querySelector(
      '.custom-tooltip-hover-display',
    );
    if (existingHover && existingHover.isConnected) {
      const parent = existingHover.parentNode;
      if (parent && parent.contains(existingHover)) {
        try {
          parent.removeChild(existingHover);
        } catch (error) {
          // Already removed
        }
      }
    }

    const tooltipText = target.getAttribute('data-quill-tooltip') || '';
    if (!tooltipText) return;

    const isInSummary = isInsideSummaryContainer(target);
    const hoverTooltip = createTooltipContainer(
      'custom-tooltip-hover-display',
      isInSummary,
    );
    hoverTooltip.textContent = tooltipText;
    addCaretToTooltip(hoverTooltip);
    document.body.appendChild(hoverTooltip);

    // Get target rect viewport
    let targetRectViewport: {
      top: number;
      left: number;
      width: number;
      height: number;
    };
    const blot = Quill.find(target);
    if (blot) {
      const index = blot.offset(quill.scroll);
      const length = blot.length();
      const bounds = quill.getBounds(index, length);
      targetRectViewport = getTargetRectViewportForQuill(
        quill,
        quillContainer,
        index,
        length,
        bounds,
      );
    } else {
      targetRectViewport = getTargetRectViewportForElement(target);
    }

    positionAndStyleTooltip(hoverTooltip, targetRectViewport);

    // Remove on mouse leave from both target and tooltip
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const removeHover = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      timeoutId = setTimeout(() => {
        if (hoverTooltip.isConnected) {
          const parent = hoverTooltip.parentNode;
          if (parent && parent.contains(hoverTooltip)) {
            try {
              parent.removeChild(hoverTooltip);
            } catch (error) {
              // Already removed
            }
          }
          target.removeEventListener('mouseleave', removeHover);
          hoverTooltip.removeEventListener('mouseenter', cancelRemove);
          hoverTooltip.removeEventListener('mouseleave', removeHover);
        }
      }, 100);
    };

    const cancelRemove = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    target.addEventListener('mouseleave', removeHover);
    hoverTooltip.addEventListener('mouseenter', cancelRemove);
    hoverTooltip.addEventListener('mouseleave', removeHover);
  }
};

// Show tooltip edit only on click
const handleTooltipClick = (
  quill: any,
  quillContainer: HTMLElement,
  e: Event,
) => {
  // Save the click coordinates
  const clickX = (e as MouseEvent).clientX;
  const clickY = (e as MouseEvent).clientY;

  setTimeout(() => {
    const newTarget = document.elementFromPoint(clickX, clickY) as HTMLElement;
    if (
      newTarget &&
      newTarget.classList.contains('custom-tooltip') &&
      newTarget.hasAttribute('data-quill-tooltip')
    ) {
      removeExistingTooltips();

      const isInSummary = isInsideSummaryContainer(newTarget);
      const tooltipBox = createTooltipContainer(
        'custom-tooltip-edit-box',
        isInSummary,
      );

      let targetRectViewport: {
        top: number;
        left: number;
        width: number;
        height: number;
      };

      const blot = Quill.find(newTarget);

      if (blot) {
        const index = blot.offset(quill.scroll);
        const length = blot.length();
        const bounds = quill.getBounds(index, length);
        targetRectViewport = getTargetRectViewportForQuill(
          quill,
          quillContainer,
          index,
          length,
          bounds,
        );
      } else {
        targetRectViewport = getTargetRectViewportForElement(newTarget);
      }

      const textarea = createAutoResizeTextarea(
        newTarget.getAttribute('data-quill-tooltip') || '',
        '',
        () => {
          positionAndStyleTooltip(tooltipBox, targetRectViewport);
        },
      );

      tooltipBox.appendChild(textarea);
      addCaretToTooltip(tooltipBox);
      document.body.appendChild(tooltipBox);

      positionAndStyleTooltip(tooltipBox, targetRectViewport);
      textarea.focus();
      textarea.select();

      let isSaved = false;
      const saveTooltip = () => {
        if (isSaved) return;
        isSaved = true;

        const newTooltip = textarea.value.trim();
        const blot = Quill.find(newTarget);
        if (blot) {
          const index = blot.offset(quill.scroll);
          const length = blot.length();

          if (newTooltip) {
            quill.formatText(index, length, 'tooltip', newTooltip);
          } else {
            const textContent = quill.getText(index, length);
            quill.setSelection(index, length);
            quill.deleteText(index, length);
            quill.insertText(index, textContent);
            quill.setSelection(index, textContent.length);
          }
        }

        if (tooltipBox.isConnected) {
          textarea.removeEventListener('keydown', keydownHandler);
          textarea.removeEventListener('blur', saveTooltip);
          const parent = tooltipBox.parentNode;
          if (parent && parent.contains(tooltipBox)) {
            try {
              parent.removeChild(tooltipBox);
            } catch (error) {
              // Already removed
            }
          }
        }
      };

      const keydownHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          saveTooltip();
        } else if (e.key === 'Escape') {
          if (isSaved) return;
          isSaved = true;

          if (tooltipBox.isConnected) {
            textarea.removeEventListener('keydown', keydownHandler);
            textarea.removeEventListener('blur', saveTooltip);
            const parent = tooltipBox.parentNode;
            if (parent && parent.contains(tooltipBox)) {
              parent.removeChild(tooltipBox);
            }
          }
        }
      };

      textarea.addEventListener('keydown', keydownHandler);
      textarea.addEventListener('blur', saveTooltip);
    }
  }, 0);
};

export const setupTooltipEditOnHover = (
  quillRef: React.RefObject<any>,
  accountVariables?: AccountVariable[],
  onCopied?: () => void,
  rtl = false,
) => {
  const quill = quillRef.current;
  if (!quill) return;
  const quillContainer = quill.container as HTMLElement;

  // Reuse handlers if already created
  const anyContainer = quillContainer as any;
  anyContainer._pointOfContactVariables = accountVariables;
  anyContainer._pointOfContactOnCopied = onCopied;
  anyContainer._pointOfContactRtl = rtl;
  if (!anyContainer._tooltipHandlers) {
    anyContainer._tooltipHandlers = {
      click: handleTooltipClick.bind(null, quill, quillContainer),
      hover: handleTooltipHover.bind(null, quill, quillContainer),
    };
  }

  const { click, hover } = anyContainer._tooltipHandlers;

  quillContainer.removeEventListener('click', click, true);
  quillContainer.removeEventListener('mouseover', hover, true);

  quillContainer.addEventListener('click', click, true);
  quillContainer.addEventListener('mouseover', hover, true);

  const handleVisibilityCheck = () => {
    checkQuillVisibility(quillContainer);
  };

  const handleDocumentVisibilityChange = () => {
    if (document.hidden) {
      closeAllTooltips();
    }
  };

  window.addEventListener('scroll', handleVisibilityCheck, true);
  document.addEventListener('visibilitychange', handleDocumentVisibilityChange);

  return () => {
    quillContainer.removeEventListener('click', click, true);
    quillContainer.removeEventListener('mouseover', hover, true);
    window.removeEventListener('scroll', handleVisibilityCheck, true);
    document.removeEventListener(
      'visibilitychange',
      handleDocumentVisibilityChange,
    );
  };
};
